import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import reportService, { type ReportResponse, type VertebralNumberingDeclaration } from '../services/reportService';
import VertebralNumberingDeclarationForm from './VertebralNumberingDeclarationForm';

interface Props {
  report: ReportResponse;
  editable: boolean;
  onUpdated: (report: ReportResponse) => void;
}

const VertebralNumberingPanel: React.FC<Props> = ({ report, editable, onUpdated }) => {
  const data = report.vertebral_numbering_data;
  const [value, setValue] = useState<VertebralNumberingDeclaration>({ level_mapping: {} });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setValue({
      basis: data.basis,
      reference_details: data.reference_details,
      variant: data.variant,
      variant_description: data.variant_description,
      level_mapping: data.level_mapping || {},
    });
  }, [data]);

  if (!data) return null;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await reportService.updateVertebralNumbering(report.id, value);
      onUpdated(updated);
    } catch (caught: unknown) {
      const failure = caught as { response?: { data?: { detail?: string } }; message?: string };
      setError(failure.response?.data?.detail || failure.message || 'Unable to update vertebral numbering');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box aria-label="Vertebral numbering review">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Vertebral Numbering</Typography>
        <Chip
          size="small"
          label={data.validation_status.toUpperCase()}
          color={data.validation_status === 'blocked' ? 'error' : data.validation_status === 'warning' ? 'warning' : 'success'}
        />
      </Stack>
      {data.detected_levels.length > 0 && (
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          {data.detected_levels.map((level) => <Chip key={level} label={level} size="small" variant="outlined" />)}
        </Stack>
      )}
      {data.issues.map((issue) => (
        <Alert key={`${issue.code}-${issue.affected_levels.join('-')}`} severity={issue.severity === 'blocking' ? 'error' : issue.severity === 'warning' ? 'warning' : 'info'} sx={{ mb: 1 }}>
          <strong>{issue.message}</strong>
          {issue.remediation && <Typography variant="caption" display="block">{issue.remediation}</Typography>}
        </Alert>
      ))}
      <Divider sx={{ my: 1.5 }} />
      <VertebralNumberingDeclarationForm value={value} onChange={setValue} basisRequired={data.basis_required} disabled={!editable || saving} />
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {editable && (
        <Button variant="contained" size="small" onClick={save} disabled={saving} sx={{ mt: 1.5 }}>
          {saving ? 'Revalidating...' : 'Save and Revalidate'}
        </Button>
      )}
      {data.last_reviewed_at && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Last reviewed {new Date(data.last_reviewed_at).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};

export default VertebralNumberingPanel;
